import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { DashboardIcon } from './ui/DashboardIcon';
import Logo from './Logo';
import Toast, { ToastType } from './Toast';
import { FleetManagerSignupResult, PasswordResetRole } from '../services/auth';

type LoginForm = {
  identifier: string;
  password: string;
};

export type FleetManagerSignupForm = {
  fullName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export type ForgotPasswordForm = {
  role: PasswordResetRole;
  email: string;
};

export type ResetPasswordForm = ForgotPasswordForm & {
  code: string;
  password: string;
  confirmPassword: string;
};

export type VerifyEmailForm = {
  email: string;
  code: string;
};

type AuthScreenProps = {
  isSubmitting: boolean;
  isPasswordResetSubmitting: boolean;
  isSignupSubmitting: boolean;
  loginForm: LoginForm;
  onChangeLoginForm: (loginForm: LoginForm) => void;
  onFleetManagerSignup: (form: FleetManagerSignupForm) => Promise<FleetManagerSignupResult>;
  onRequestPasswordReset: (form: ForgotPasswordForm) => Promise<void>;
  onResendSignupVerification: (email: string) => Promise<void>;
  onResetPassword: (form: ResetPasswordForm) => Promise<void>;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onVerifySignupEmail: (form: VerifyEmailForm) => Promise<void>;
  toast: {
    message: string;
    type: ToastType;
    visible: boolean;
  };
  verificationEmail?: string;
};

export function AuthScreen({
  isSubmitting,
  isPasswordResetSubmitting,
  isSignupSubmitting,
  loginForm,
  onChangeLoginForm,
  onFleetManagerSignup,
  onRequestPasswordReset,
  onResendSignupVerification,
  onResetPassword,
  onSubmit,
  onVerifySignupEmail,
  toast,
  verificationEmail = '',
}: AuthScreenProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [mode, setMode] = React.useState<'login' | 'fleet-signup' | 'forgot-password' | 'verify-email'>('login');
  const [signupForm, setSignupForm] = React.useState<FleetManagerSignupForm>({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [signupError, setSignupError] = React.useState('');
  const [signupSuccess, setSignupSuccess] = React.useState('');
  const [verifyForm, setVerifyForm] = React.useState<VerifyEmailForm>({ email: '', code: '' });
  const [verifyError, setVerifyError] = React.useState('');
  const [verifyMessage, setVerifyMessage] = React.useState('');
  const [forgotStep, setForgotStep] = React.useState<'request' | 'reset'>('request');
  const [forgotForm, setForgotForm] = React.useState<ResetPasswordForm>({
    role: 'fleet_manager',
    email: '',
    code: '',
    password: '',
    confirmPassword: '',
  });
  const [forgotError, setForgotError] = React.useState('');
  const [forgotMessage, setForgotMessage] = React.useState('');

  React.useEffect(() => {
    if (!verificationEmail) {
      return;
    }

    setVerifyForm({ email: verificationEmail, code: '' });
    setVerifyMessage('Verify your email before signing in.');
    setVerifyError('');
    setMode('verify-email');
  }, [verificationEmail]);

  const submitSignup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSignupError('');
    setSignupSuccess('');

    if (signupForm.password.length < 8) {
      setSignupError('Password must be at least 8 characters.');
      return;
    }

    if (signupForm.password !== signupForm.confirmPassword) {
      setSignupError('Password and confirmation do not match.');
      return;
    }

    try {
      const result = await onFleetManagerSignup(signupForm);
      setVerifyForm({ email: signupForm.email.trim(), code: '' });
      setVerifyMessage(result.message || 'Signup successful. Enter the verification code sent to your email.');
      setSignupForm((current) => ({ ...current, password: '', confirmPassword: '' }));
      setMode('verify-email');
    } catch (error) {
      setSignupError(error instanceof Error ? error.message : 'Unable to create fleet manager account');
    }
  };

  const submitEmailVerification = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setVerifyError('');
    setVerifyMessage('');

    try {
      await onVerifySignupEmail({ email: verifyForm.email.trim(), code: verifyForm.code.trim() });
      setSignupSuccess('Email verified successfully. You can now sign in and submit your fleet KYC.');
      setMode('login');
      setVerifyForm((current) => ({ ...current, code: '' }));
    } catch (error) {
      setVerifyError(error instanceof Error ? error.message : 'Unable to verify email');
    }
  };

  const resendEmailVerification = async () => {
    setVerifyError('');
    setVerifyMessage('');

    try {
      await onResendSignupVerification(verifyForm.email.trim());
      setVerifyMessage('A new verification code has been sent.');
    } catch (error) {
      setVerifyError(error instanceof Error ? error.message : 'Unable to resend verification code');
    }
  };

  const submitForgotPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setForgotError('');
    setForgotMessage('');

    if (forgotStep === 'request') {
      try {
        await onRequestPasswordReset({ email: forgotForm.email, role: forgotForm.role });
        setForgotMessage('If the email exists, a reset code has been sent. Enter it below to set a new password.');
        setForgotStep('reset');
      } catch (error) {
        setForgotError(error instanceof Error ? error.message : 'Unable to request password reset');
      }
      return;
    }

    if (forgotForm.password.length < 8) {
      setForgotError('New password must be at least 8 characters.');
      return;
    }

    if (forgotForm.password !== forgotForm.confirmPassword) {
      setForgotError('New password and confirmation do not match.');
      return;
    }

    try {
      await onResetPassword(forgotForm);
      setSignupSuccess('Password reset successfully. You can now sign in.');
      setForgotForm((current) => ({
        ...current,
        code: '',
        password: '',
        confirmPassword: '',
      }));
      setMode('login');
      setForgotStep('request');
    } catch (error) {
      setForgotError(error instanceof Error ? error.message : 'Unable to reset password');
    }
  };

  return (
    <Box className="auth-shell">
      <Box className="auth-side" component="section">
        <div className="auth-brand">
          <Logo />
        </div>
        <h1>One platform.<br />Every operator.</h1>
        <p>Manage your fleet, oversee operations, or respond to incidents - all from one secure portal.</p>
        <div className="auth-roles">
          <div className="ar-item">
            <span className="ar-icon green">
              <RoleIcon type="fleet" size={20} />
            </span>
            <span>
              <strong>Fleet Manager</strong>
              <span>Drivers, vehicles, live map</span>
            </span>
          </div>
          <div className="ar-item">
            <span className="ar-icon gold">
              <RoleIcon type="admin" size={20} />
            </span>
            <span>
              <strong>Administrator</strong>
              <span>Users, KYC, transactions</span>
            </span>
          </div>
          <div className="ar-item">
            <span className="ar-icon red">
              <RoleIcon type="law" size={20} />
            </span>
            <span>
              <strong>Law Enforcement</strong>
              <span>SOS incident response</span>
            </span>
          </div>
        </div>
      </Box>

      <Box className="auth-form" component="section">
        {mode === 'login' ? (
        <Paper className="auth-card" component="form" elevation={2} onSubmit={onSubmit} aria-busy={isSubmitting}>
          <span className="auth-eyebrow">Operator portal</span>
          <h2>Sign in to your account</h2>
          <p className="muted">Use your operator credentials</p>
          {signupSuccess ? <div className="kyc-message" role="status">{signupSuccess}</div> : null}
          <div className="auth-fields">
            <TextField
              autoComplete="username"
              disabled={isSubmitting}
              fullWidth
              id="loginEmail"
              label="Username or email"
              onChange={(event) => onChangeLoginForm({ ...loginForm, identifier: event.target.value })}
              required
              value={loginForm.identifier}
            />
            <TextField
              autoComplete="current-password"
              disabled={isSubmitting}
              fullWidth
              id="loginPassword"
              label="Password"
              onChange={(event) => onChangeLoginForm({ ...loginForm, password: event.target.value })}
              required
              type={showPassword ? 'text' : 'password'}
              value={loginForm.password}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isSubmitting}
                        edge="end"
                onClick={() => setShowPassword((current) => !current)}
                        size="small"
              >
                <DashboardIcon type="eye" size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>

          <Button
            className="auth-submit"
            disabled={isSubmitting}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>

          <Button
            className="auth-link-button"
            disabled={isSubmitting}
            onClick={() => {
              setMode('forgot-password');
              setForgotError('');
              setForgotMessage('');
              setForgotStep('request');
            }}
            variant="text"
          >
            Forgot password?
          </Button>

          <Button
            className="auth-link-button"
            disabled={isSubmitting}
            onClick={() => {
              setMode('fleet-signup');
              setSignupError('');
              setSignupSuccess('');
            }}
            variant="outlined"
          >
            Create an operator account
          </Button>

        </Paper>
        ) : mode === 'verify-email' ? (
        <Paper className="auth-card" component="form" elevation={2} onSubmit={submitEmailVerification}>
          <h2>Verify your email</h2>
          <p className="muted">Enter the code sent to your email address before signing in.</p>
          {verifyError ? <div className="kyc-message passengers-error" role="alert">{verifyError}</div> : null}
          {verifyMessage ? <div className="kyc-message" role="status">{verifyMessage}</div> : null}
          <div className="form-group">
            <label htmlFor="verifyEmail">Email</label>
            <input
              autoComplete="email"
              id="verifyEmail"
              onChange={(event) => setVerifyForm((current) => ({ ...current, email: event.target.value }))}
              required
              type="email"
              value={verifyForm.email}
            />
          </div>
          <div className="form-group">
            <label htmlFor="verifyCode">Verification code</label>
            <input
              autoComplete="one-time-code"
              id="verifyCode"
              onChange={(event) => setVerifyForm((current) => ({ ...current, code: event.target.value }))}
              required
              type="text"
              value={verifyForm.code}
            />
          </div>
          <button className="btn btn-primary btn-lg btn-block" type="submit">
            Verify email
          </button>
          <button
            className="auth-link-button"
            onClick={() => void resendEmailVerification()}
            type="button"
          >
            Resend code
          </button>
          <button
            className="auth-link-button"
            onClick={() => {
              setMode('login');
              setVerifyError('');
              setVerifyMessage('');
            }}
            type="button"
          >
            Back to sign in
          </button>
        </Paper>
        ) : mode === 'forgot-password' ? (
        <Paper className="auth-card" component="form" elevation={2} onSubmit={submitForgotPassword} aria-busy={isPasswordResetSubmitting}>
          <h2>Reset password</h2>
          <p className="muted">Password reset is available for admin, super admin, fleet manager, and law enforcement accounts.</p>
          {forgotError ? <div className="kyc-message passengers-error" role="alert">{forgotError}</div> : null}
          {forgotMessage ? <div className="kyc-message" role="status">{forgotMessage}</div> : null}
          <div className="form-group">
            <label htmlFor="forgotRole">Account type</label>
            <select
              disabled={isPasswordResetSubmitting || forgotStep === 'reset'}
              id="forgotRole"
              onChange={(event) => setForgotForm((current) => ({ ...current, role: event.target.value as PasswordResetRole }))}
              value={forgotForm.role}
            >
              <option value="fleet_manager">Fleet manager</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super admin</option>
              <option value="law_enforcement">Law enforcement</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="forgotEmail">Email</label>
            <input
              autoComplete="email"
              disabled={isPasswordResetSubmitting || forgotStep === 'reset'}
              id="forgotEmail"
              onChange={(event) => setForgotForm((current) => ({ ...current, email: event.target.value }))}
              required
              type="email"
              value={forgotForm.email}
            />
          </div>

          {forgotStep === 'reset' ? (
            <>
              <div className="form-group">
                <label htmlFor="resetCode">Reset code</label>
                <input
                  autoComplete="one-time-code"
                  disabled={isPasswordResetSubmitting}
                  id="resetCode"
                  onChange={(event) => setForgotForm((current) => ({ ...current, code: event.target.value }))}
                  required
                  type="text"
                  value={forgotForm.code}
                />
              </div>
              <div className="form-group">
                <label htmlFor="resetPassword">New password</label>
                <div className="password-field">
                  <input
                    autoComplete="new-password"
                    disabled={isPasswordResetSubmitting}
                    id="resetPassword"
                    minLength={8}
                    onChange={(event) => setForgotForm((current) => ({ ...current, password: event.target.value }))}
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={forgotForm.password}
                  />
                  <button
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="password-toggle"
                    disabled={isPasswordResetSubmitting}
                    onClick={() => setShowPassword((current) => !current)}
                    type="button"
                  >
                    <DashboardIcon type="eye" size={18} />
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="resetConfirmPassword">Confirm new password</label>
                <input
                  autoComplete="new-password"
                  disabled={isPasswordResetSubmitting}
                  id="resetConfirmPassword"
                  minLength={8}
                  onChange={(event) => setForgotForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                  required
                  type={showPassword ? 'text' : 'password'}
                  value={forgotForm.confirmPassword}
                />
              </div>
            </>
          ) : null}

          <button className="btn btn-primary btn-lg btn-block" disabled={isPasswordResetSubmitting} type="submit">
            {isPasswordResetSubmitting
              ? 'Please wait...'
              : forgotStep === 'request' ? 'Send reset code' : 'Reset password'}
          </button>
          {forgotStep === 'reset' ? (
            <button
              className="auth-link-button"
              disabled={isPasswordResetSubmitting}
              onClick={() => {
                setForgotStep('request');
                setForgotMessage('');
                setForgotError('');
              }}
              type="button"
            >
              Use a different email
            </button>
          ) : null}
          <button
            className="auth-link-button"
            disabled={isPasswordResetSubmitting}
            onClick={() => {
              setMode('login');
              setForgotError('');
              setForgotMessage('');
            }}
            type="button"
          >
            Back to sign in
          </button>
        </Paper>
        ) : (
        <Paper className="auth-card" component="form" elevation={2} onSubmit={submitSignup} aria-busy={isSignupSubmitting}>
          <h2>Create fleet manager account</h2>
          <p className="muted">Register your operator profile, then complete fleet onboarding after email verification.</p>
          {signupError ? <div className="kyc-message passengers-error" role="alert">{signupError}</div> : null}
          <div className="form-group">
            <label htmlFor="signupFullName">Full name</label>
            <input
              autoComplete="name"
              disabled={isSignupSubmitting}
              id="signupFullName"
              onChange={(event) => setSignupForm((current) => ({ ...current, fullName: event.target.value }))}
              required
              type="text"
              value={signupForm.fullName}
            />
          </div>
          <div className="form-group">
            <label htmlFor="signupUsername">Username</label>
            <input
              autoComplete="username"
              disabled={isSignupSubmitting}
              id="signupUsername"
              onChange={(event) => setSignupForm((current) => ({ ...current, username: event.target.value }))}
              required
              type="text"
              value={signupForm.username}
            />
          </div>
          <div className="form-group">
            <label htmlFor="signupEmail">Email</label>
            <input
              autoComplete="email"
              disabled={isSignupSubmitting}
              id="signupEmail"
              onChange={(event) => setSignupForm((current) => ({ ...current, email: event.target.value }))}
              required
              type="email"
              value={signupForm.email}
            />
          </div>
          <div className="form-group">
            <label htmlFor="signupPassword">Password</label>
            <div className="password-field">
              <input
                autoComplete="new-password"
                disabled={isSignupSubmitting}
                id="signupPassword"
                minLength={8}
                onChange={(event) => setSignupForm((current) => ({ ...current, password: event.target.value }))}
                required
                type={showPassword ? 'text' : 'password'}
                value={signupForm.password}
              />
              <button
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="password-toggle"
                disabled={isSignupSubmitting}
                onClick={() => setShowPassword((current) => !current)}
                type="button"
              >
                <DashboardIcon type="eye" size={18} />
              </button>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="signupConfirmPassword">Confirm password</label>
            <input
              autoComplete="new-password"
              disabled={isSignupSubmitting}
              id="signupConfirmPassword"
              minLength={8}
              onChange={(event) => setSignupForm((current) => ({ ...current, confirmPassword: event.target.value }))}
              required
              type={showPassword ? 'text' : 'password'}
              value={signupForm.confirmPassword}
            />
          </div>
          <button className="btn btn-primary btn-lg btn-block" disabled={isSignupSubmitting} type="submit">
            {isSignupSubmitting ? 'Creating account...' : 'Create account'}
          </button>
          <button
            className="auth-link-button"
            disabled={isSignupSubmitting}
            onClick={() => {
              setMode('login');
              setSignupError('');
            }}
            type="button"
          >
            Back to sign in
          </button>
        </Paper>
        )}
      </Box>

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </Box>
  );
}

type RoleIconProps = {
  size: number;
  type: 'fleet' | 'admin' | 'admin-check' | 'super-admin' | 'law';
};

function RoleIcon({ size, type }: RoleIconProps) {
  const paths: Record<RoleIconProps['type'], React.ReactNode> = {
    fleet: (
      <>
        <rect x="2" y="6" width="20" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="7" cy="20" r="2.5" fill="currentColor" />
        <circle cx="17" cy="20" r="2.5" fill="currentColor" />
      </>
    ),
    admin: (
      <>
        <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M4 21v-1a6 6 0 0112 0v1" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M16 11l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
    'admin-check': (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
    'super-admin': (
      <path d="M12 2L4 7v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V7l-8-5z" fill="none" stroke="currentColor" strokeWidth="2" />
    ),
    law: (
      <>
        <path d="M12 2L2 22h20L12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 10v4M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {paths[type]}
    </svg>
  );
}
